import React, { type ComponentType, type ReactElement, type ReactNode } from "react";
import { AlertTriangle, Pencil, UserMinus } from "lucide-react";
import {
  Button,
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Disclosure,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  cn,
} from "@ndd/ui";
import {
  RD_CLOSE_ACTION,
  RD_LINKED_CLOSE_LINE,
  RD_LINKED_HEALTHIE_HIDDEN_NOTE,
  RD_LINKED_HEALTHIE_NOTE,
  RD_LINKED_SIGN_IN_HIDDEN_NOTE,
  RD_PROVIDER_LINKS_LABEL,
  RD_REMOVE_ACTION,
  RD_TOMBSTONED_SECTION_TITLE,
  RD_TOMBSTONE_ACTION,
  RD_TOMBSTONE_PENDING,
  RD_TOMBSTONE_REASON_LABEL,
  RD_TOMBSTONE_REASON_PLACEHOLDER,
  rdLinkedBody,
  rdLinkedSignInNote,
  rdLinkedTitle,
  rdTombstoneBody,
  rdTombstoneNameLabel,
  rdTombstoneTitle,
  registryTwinMessage,
  type RdLinkedNoteParts,
  tombstoneNameMatches,
  tombstoneReasonIsValid,
} from "../../../../shared/rd-registry-copy";
import type { RdRemoval } from "../../../../shared/rd-registry-removal";
import { formatDate } from "../../lib/format";
import { avatarClass, initials } from "../../lib/avatar";
import {
  NDD_ADMIN_PROVIDER_LINKS_URL,
  groupRegistryIntoLanes,
  registryRollup,
  type RegistryLaneGroup,
  type RegistryLinkFacts,
} from "../../lib/rd-registry-lanes";
import { RDAvatar } from "./rd-avatar";
import { RdIdentityLine } from "./rd-identity-line";

export interface RegistryRdView extends RegistryLinkFacts {
  id: string;
  name: string;
  supervisorName: string | null;
  sessionCount: number;
  /** The server's removal state; the Remove dialog switches on its kind. */
  removal: RdRemoval;
}

interface RegistryRowActions<Row> {
  onEdit: (rd: Row) => void;
  /** NO-695: one Remove control on every live row; the dialog follows `removal`. */
  onRemove: (rd: Row) => void;
}

const GRID_CLASS = "grid grid-cols-[3fr_2fr_1fr_auto] gap-4 px-5";

const ACTION_CLASS =
  "inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors";

export function RegistryPageHead({
  action,
}: { action?: ReactNode } = {}): ReactElement {
  return (
    <div className="flex items-start justify-between gap-4 mb-6">
      <div>
        <div className="text-caption uppercase tracking-wider text-muted-foreground font-medium">
          Admin
        </div>
        <h1 className="text-2xl font-bold font-sans text-foreground">RDs</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Every RD, grouped by whether their sign-in account and Healthie record are
          linked. Links are made in NDD Admin.
        </p>
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function RegistryRollupStrip({
  rows,
}: {
  rows: RegistryLinkFacts[];
}): ReactElement {
  const rollup = registryRollup(rows);
  const cells = [
    { label: "RDs", value: rollup.total, warn: false },
    { label: "Need a link", value: rollup.needLink, warn: true },
    { label: "No supervisor", value: rollup.noSupervisor, warn: true },
    { label: "Linked", value: rollup.linked, warn: false },
  ];

  return (
    <dl className="mb-4 grid grid-cols-2 md:grid-cols-4 rounded-xl border border-border bg-card shadow-sm divide-y md:divide-y-0 md:divide-x divide-border">
      {cells.map((cell) => (
        <div key={cell.label} className="px-5 py-4">
          <dt className="text-caption font-medium uppercase tracking-wider text-muted-foreground">
            {cell.label}
          </dt>
          <dd
            className={cn(
              "mt-1 text-xl font-bold",
              cell.warn ? "text-warning" : "text-foreground"
            )}
          >
            {cell.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function RegistryColumnHeader(): ReactElement {
  return (
    <div
      className={cn(
        GRID_CLASS,
        "text-caption tracking-wider text-muted-foreground font-medium uppercase"
      )}
    >
      <div>RD</div>
      <div>Supervisor</div>
      <div>Sessions</div>
      <div className="text-right">Actions</div>
    </div>
  );
}

function ProviderLinksNote(): ReactElement {
  return (
    <span className="font-normal normal-case tracking-normal text-body-sm text-muted-foreground">
      · made on{" "}
      <a
        href={NDD_ADMIN_PROVIDER_LINKS_URL}
        target="_blank"
        rel="noreferrer"
        className="text-primary hover:underline"
      >
        Provider Links in NDD Admin
      </a>
    </span>
  );
}

function RegistryLaneHeader({
  lane,
}: {
  lane: RegistryLaneGroup<RegistryLinkFacts>;
}): ReactElement {
  return (
    <h2 className="flex flex-wrap items-center gap-2 py-3 px-5 bg-muted border-b border-border text-xs tracking-wider text-muted-foreground font-semibold uppercase">
      {lane.label}
      <span className="font-medium text-subtle">{lane.rows.length}</span>
      {lane.lane === "needs-link" && <ProviderLinksNote />}
    </h2>
  );
}

function UnmappedPill(): ReactElement {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-warning-soft text-warning text-micro font-semibold uppercase tracking-wider rounded-full">
      <AlertTriangle className="h-3 w-3" />
      Unmapped
    </span>
  );
}

function RegistryRdIdentity({ rd }: { rd: RegistryRdView }): ReactElement {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <RDAvatar name={rd.name} />
      <div className="min-w-0">
        <div className="text-body text-foreground font-medium flex items-center gap-2">
          {rd.name}
          {!rd.supervisorId && <UnmappedPill />}
        </div>
        <RdIdentityLine
          rdName={rd.name}
          signInAccount={rd.signInAccount}
          healthieRecordUrl={rd.healthieRecordUrl}
        />
      </div>
    </div>
  );
}

function RegistryRowButtons({
  onEdit,
  onRemove,
}: {
  onEdit: () => void;
  onRemove: () => void;
}): ReactElement {
  return (
    <div className="flex justify-end gap-1">
      <button
        type="button"
        onClick={onEdit}
        className={cn(ACTION_CLASS, "hover:bg-muted hover:text-foreground")}
        aria-label="Edit"
        title="Edit RD"
      >
        <Pencil className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={onRemove}
        className={cn(ACTION_CLASS, "hover:bg-muted hover:text-foreground")}
        aria-label={RD_REMOVE_ACTION}
        title={RD_REMOVE_ACTION}
      >
        <UserMinus className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

function SupervisorCell({ name }: { name: string | null }): ReactElement {
  return (
    <div className="text-label text-muted-strong">
      {name ? name : <span className="text-subtle italic">None</span>}
    </div>
  );
}

function RegistryRow<Row extends RegistryRdView>({
  rd,
  actions,
}: {
  rd: Row;
  actions: RegistryRowActions<Row>;
}): ReactElement {
  return (
    <div
      className={cn(
        GRID_CLASS,
        "py-3 border-b border-sand-100 last:border-b-0 items-center"
      )}
    >
      <RegistryRdIdentity rd={rd} />
      <SupervisorCell name={rd.supervisorName} />
      <div className="text-label text-muted-strong">{rd.sessionCount}</div>
      <RegistryRowButtons
        onEdit={() => actions.onEdit(rd)}
        onRemove={() => actions.onRemove(rd)}
      />
    </div>
  );
}

const CARD_CLASS = "rounded-xl border border-border bg-card shadow-sm overflow-hidden";

function RegistryLaneCard<Row extends RegistryRdView>({
  lane,
  actions,
}: {
  lane: RegistryLaneGroup<Row>;
  actions: RegistryRowActions<Row>;
}): ReactElement {
  return (
    <section aria-label={lane.label} className={CARD_CLASS}>
      <RegistryLaneHeader lane={lane} />
      {lane.rows.map((rd) => (
        <RegistryRow key={rd.id} rd={rd} actions={actions} />
      ))}
    </section>
  );
}

/** One column header above a separate card per link lane, as on the RD roster. */
export function RegistryLanes<Row extends RegistryRdView>({
  rows,
  isLoading,
  ...actions
}: RegistryRowActions<Row> & { rows: Row[]; isLoading: boolean }): ReactElement {
  const lanes = groupRegistryIntoLanes(rows);

  return (
    <div className="space-y-4">
      <RegistryColumnHeader />
      {!isLoading && rows.length === 0 && (
        <div
          className={cn(CARD_CLASS, "p-8 text-center text-muted-foreground text-sm")}
        >
          No RDs yet.
        </div>
      )}
      {lanes.map((lane) => (
        <RegistryLaneCard key={lane.lane} lane={lane} actions={actions} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add RD dialog (NO-651, Figma 2:2 frames 3:165, 3:222, 3:253). Step 2 departs
// from frame 3:222 by human decision (QA, 2026-09-23): no helper sentence, and a
// registry twin is refused on open, before any submit. Presentational
// step bodies only: the container in routes/admin/rds.tsx owns state, queries
// and the mutation. Rendered inside DialogContent there; the header slots take
// DialogTitle/DialogDescription so the dialog keeps its accessible name.
// ---------------------------------------------------------------------------

export interface EnrollableProviderView {
  id: string;
  name: string;
  firstName: string | null;
  email: string | null;
  /** The registry RD this provider twins, by name; null when none. */
  twinOf: string | null;
}

export interface SupervisorChoice {
  id: string;
  name: string;
}

type TextSlot = ComponentType<{ children?: ReactNode; className?: string }>;

export interface AddRdHeaderSlots {
  Title?: TextSlot;
  Description?: TextSlot;
}

export const ADD_RD_TITLE = "Add an RD";
export const ADD_RD_SUBTITLE = "Enroll an existing Healthie provider.";

export function providerEmailLabel(provider: EnrollableProviderView): string {
  return provider.email ?? "No email in Healthie";
}

function PlainTitle({
  children,
  className,
}: {
  children?: ReactNode;
  className?: string;
}) {
  return <h2 className={className}>{children}</h2>;
}

function PlainDescription({
  children,
  className,
}: {
  children?: ReactNode;
  className?: string;
}) {
  return <p className={className}>{children}</p>;
}

function AddRdHeader({ slots }: { slots?: AddRdHeaderSlots }): ReactElement {
  const Title = slots?.Title ?? PlainTitle;
  const Description = slots?.Description ?? PlainDescription;

  return (
    <div className="flex flex-col gap-1">
      <Title className="text-lg font-semibold text-foreground">{ADD_RD_TITLE}</Title>
      <Description className="text-sm text-muted-foreground">
        {ADD_RD_SUBTITLE}
      </Description>
    </div>
  );
}

const PILL_BASE = "rounded-full px-2.5 py-1 text-meta font-medium";
const PILL_ACTIVE = "bg-bark text-white";
const PILL_MUTED = "bg-muted text-muted-foreground";

export function AddRdStepPills({
  step,
  providerName,
}: {
  step: 1 | 2;
  providerName?: string;
}): ReactElement {
  const first = step === 1 ? "1 · Provider" : `1 · ${providerName} ✓`;

  return (
    <div className="flex items-center gap-2" aria-label="Steps">
      <span className={cn(PILL_BASE, step === 1 ? PILL_ACTIVE : PILL_MUTED)}>
        {first}
      </span>
      <span className={cn(PILL_BASE, step === 2 ? PILL_ACTIVE : PILL_MUTED)}>
        2 · Supervisor
      </span>
    </div>
  );
}

function ProviderRow({
  provider,
  selected,
  onSelect,
}: {
  provider: EnrollableProviderView;
  selected: boolean;
  onSelect: (id: string) => void;
}): ReactElement {
  return (
    <CommandItem
      value={provider.id}
      onSelect={() => onSelect(provider.id)}
      aria-selected={selected}
      className={cn(
        "flex items-center gap-2.5 rounded-[7px] px-2.5 py-2",
        selected && "bg-primary-soft"
      )}
    >
      <span
        className={cn(
          "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-micro font-semibold text-white",
          avatarClass(provider.id)
        )}
      >
        {initials(provider.name)}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-body font-medium text-foreground">
          {provider.name}
        </span>
        <span className="block truncate text-meta text-muted-foreground">
          {providerEmailLabel(provider)}
        </span>
      </span>
    </CommandItem>
  );
}

const PANEL_CLASS = "overflow-hidden rounded-[10px] border border-border";
const GROUP_HEADER_CLASS =
  "[&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider";

export interface AddRdProviderStepProps {
  providers: EnrollableProviderView[];
  query: string;
  onQueryChange: (value: string) => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onCancel: () => void;
  onNext: () => void;
  headerSlots?: AddRdHeaderSlots;
}

/** Step 1 (frame 3:165). `providers` is the list as currently filtered. */
export function AddRdProviderStep(props: AddRdProviderStepProps): ReactElement {
  const { providers, selectedId } = props;

  return (
    <div className="flex flex-col gap-4">
      <AddRdHeader slots={props.headerSlots} />
      <AddRdStepPills step={1} />
      <div className={PANEL_CLASS}>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search Healthie providers…"
            value={props.query}
            onValueChange={props.onQueryChange}
          />
          <CommandList>
            <CommandGroup
              heading={`Ready to enroll · ${providers.length}`}
              className={GROUP_HEADER_CLASS}
            >
              {providers.map((provider) => (
                <ProviderRow
                  key={provider.id}
                  provider={provider}
                  selected={provider.id === selectedId}
                  onSelect={props.onSelect}
                />
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
        <p className="border-t border-border bg-background px-3 py-2 text-meta text-muted-foreground">
          Someone missing? They need a Healthie record first, then they appear here.
        </p>
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={props.onCancel}>
          Cancel
        </Button>
        <Button type="button" onClick={props.onNext} disabled={!selectedId}>
          Next
        </Button>
      </div>
    </div>
  );
}

export interface AddRdSupervisorStepProps {
  provider: EnrollableProviderView;
  supervisors: SupervisorChoice[];
  supervisorId: string;
  onSupervisorChange: (id: string) => void;
  error?: string | null;
  isPending?: boolean;
  onBack: () => void;
  onSubmit: () => void;
  headerSlots?: AddRdHeaderSlots;
}

const NOTICE_CLASS =
  "rounded-lg border border-warning/30 bg-warning-soft px-3 py-2 text-sm text-foreground";

/**
 * Step 2 (frame 3:222, amended). A registry twin shows its refusal as soon as
 * the step opens, with the supervisor and Add RD disabled; the twin notice then
 * stands in for any submit error, so the text never shows twice.
 */
export function AddRdSupervisorStep(props: AddRdSupervisorStepProps): ReactElement {
  const { provider, supervisorId } = props;
  const supervisor = props.supervisors.find((choice) => choice.id === supervisorId);
  const isTwin = provider.twinOf !== null;

  return (
    <div className="flex flex-col gap-4">
      <AddRdHeader slots={props.headerSlots} />
      <AddRdStepPills step={2} providerName={provider.name} />
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="add-rd-supervisor" className="text-label font-medium">
          Supervisor
        </Label>
        <Select
          value={supervisorId || undefined}
          onValueChange={props.onSupervisorChange}
          disabled={isTwin}
        >
          <SelectTrigger id="add-rd-supervisor" className="w-full">
            <SelectValue placeholder="Select a supervisor" />
          </SelectTrigger>
          <SelectContent>
            {props.supervisors.map((choice) => (
              <SelectItem key={choice.id} value={choice.id}>
                {choice.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {provider.twinOf !== null && (
          <div className={NOTICE_CLASS}>{registryTwinMessage(provider.twinOf)}</div>
        )}
      </div>
      {props.error && !isTwin && <div className={NOTICE_CLASS}>{props.error}</div>}
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={props.onBack}
          disabled={props.isPending}
        >
          Back
        </Button>
        <Button
          type="button"
          onClick={props.onSubmit}
          disabled={isTwin || !supervisor || props.isPending}
        >
          Add RD
        </Button>
      </div>
    </div>
  );
}

/** Frame 3:253: shown only when no provider is enrollable at all. */
export function AddRdEmptyState({
  onClose,
  headerSlots,
}: {
  onClose: () => void;
  headerSlots?: AddRdHeaderSlots;
}): ReactElement {
  return (
    <div className="flex flex-col gap-4">
      <AddRdHeader slots={headerSlots} />
      <AddRdStepPills step={1} />
      <div className={PANEL_CLASS}>
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search Healthie providers…" disabled />
          <CommandList>
            <div className="flex flex-col items-center gap-1 px-6 py-8 text-center">
              <p className="text-body font-medium text-foreground">
                Nobody is ready to enroll
              </p>
              <p className="text-label text-muted-foreground">
                Every Healthie provider is already an RD. Add a new RD in Healthie
                first, then link them on Provider Links in NDD Admin.
              </p>
            </div>
          </CommandList>
        </Command>
      </div>
      <div className="flex justify-end">
        <Button type="button" variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// RD Tombstone (NO-695). Presentational parts only: the container in
// routes/admin/rds.tsx owns state, queries and the mutation. Rendered inside
// DialogContent there; the header slots keep the dialog's accessible name.
// ---------------------------------------------------------------------------

export interface TombstoneRefusal {
  code?: string;
  error: string;
}

const SIGN_IN_LINKED_CODE = "rd_sign_in_linked";

/** A server refusal, shown inline; the sign-in refusal links Provider Links. */
export function TombstoneRefusalNotice({
  refusal,
}: {
  refusal: TombstoneRefusal;
}): ReactElement {
  const [before, after] = refusal.error.split(RD_PROVIDER_LINKS_LABEL);
  const linksProviderLinks =
    refusal.code === SIGN_IN_LINKED_CODE && after !== undefined;

  return (
    <div role="alert" className={NOTICE_CLASS}>
      {linksProviderLinks ? (
        <>
          {before}
          <a
            href={NDD_ADMIN_PROVIDER_LINKS_URL}
            target="_blank"
            rel="noreferrer"
            className="text-primary hover:underline"
          >
            {RD_PROVIDER_LINKS_LABEL}
          </a>
          {after}
        </>
      ) : (
        refusal.error
      )}
    </div>
  );
}

export interface TombstoneRdDialogBodyProps {
  rdName: string;
  typedName: string;
  onTypedNameChange: (value: string) => void;
  reason: string;
  onReasonChange: (value: string) => void;
  refusal: TombstoneRefusal | null;
  /** `rdHistoryKeptLine(deleteEligibility.blockers)`: muted text, not a note. */
  historyLine: string | null;
  isPending?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  headerSlots?: AddRdHeaderSlots;
}

/** Confirm stays unavailable until the exact name and a reason are both in (AC2). */
export function tombstoneConfirmEnabled(
  props: Pick<
    TombstoneRdDialogBodyProps,
    "rdName" | "typedName" | "reason" | "isPending"
  >
): boolean {
  const ready =
    tombstoneNameMatches(props.typedName, props.rdName) &&
    tombstoneReasonIsValid(props.reason);

  return ready && !props.isPending;
}

function TombstoneHeader({
  rdName,
  slots,
}: {
  rdName: string;
  slots?: AddRdHeaderSlots;
}): ReactElement {
  const Title = slots?.Title ?? PlainTitle;
  const Description = slots?.Description ?? PlainDescription;

  return (
    <div className="flex flex-col gap-1">
      <Title className="text-lg font-semibold text-foreground">
        {rdTombstoneTitle(rdName)}
      </Title>
      <Description className="text-sm text-muted-foreground">
        {rdTombstoneBody(rdName)}
      </Description>
    </div>
  );
}

function TombstoneFields(props: TombstoneRdDialogBodyProps): ReactElement {
  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="tombstone-rd-name" className="text-label font-medium">
          {rdTombstoneNameLabel(props.rdName)}
        </Label>
        <Input
          id="tombstone-rd-name"
          value={props.typedName}
          onChange={(event) => props.onTypedNameChange(event.target.value)}
          autoComplete="off"
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="tombstone-rd-reason" className="text-label font-medium">
          {RD_TOMBSTONE_REASON_LABEL}
        </Label>
        <Textarea
          id="tombstone-rd-reason"
          placeholder={RD_TOMBSTONE_REASON_PLACEHOLDER}
          value={props.reason}
          onChange={(event) => props.onReasonChange(event.target.value)}
          required
        />
      </div>
    </>
  );
}

/** The Tombstone dialog's body: the exact-name Input, the reason, and Confirm. */
export function TombstoneRdDialogBody(props: TombstoneRdDialogBodyProps): ReactElement {
  return (
    <div className="flex flex-col gap-4">
      <TombstoneHeader rdName={props.rdName} slots={props.headerSlots} />
      {props.historyLine && (
        <p className="text-body-sm text-muted-foreground">{props.historyLine}</p>
      )}
      <TombstoneFields {...props} />
      {props.refusal && <TombstoneRefusalNotice refusal={props.refusal} />}
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={props.onCancel}
          disabled={props.isPending}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="destructive"
          onClick={props.onConfirm}
          disabled={!tombstoneConfirmEnabled(props)}
        >
          {props.isPending ? RD_TOMBSTONE_PENDING : RD_TOMBSTONE_ACTION}
        </Button>
      </div>
    </div>
  );
}

export type LinkedRemoval = Extract<RdRemoval, { kind: "linked" }>;

export interface LinkedRdDialogBodyProps extends Pick<
  RegistryLinkFacts,
  "signInAccount" | "healthieRecordUrl"
> {
  rdName: string;
  removal: LinkedRemoval;
  onClose: () => void;
  headerSlots?: AddRdHeaderSlots;
}

const LINK_CLASS = "text-primary hover:underline";

function LinkedNote({
  parts,
  href,
}: {
  parts: RdLinkedNoteParts;
  href: string;
}): ReactElement {
  return (
    <div className={NOTICE_CLASS}>
      {parts.lead}
      <a href={href} target="_blank" rel="noreferrer" className={LINK_CLASS}>
        {parts.link}
      </a>
      {parts.tail}
    </div>
  );
}

/** The sign-in note: the account the row shows, or the hidden wording when it shows none. */
function SignInLinkedNote({ props }: { props: LinkedRdDialogBodyProps }): ReactElement {
  const parts = props.signInAccount
    ? rdLinkedSignInNote(props.signInAccount.email)
    : RD_LINKED_SIGN_IN_HIDDEN_NOTE;

  return <LinkedNote parts={parts} href={NDD_ADMIN_PROVIDER_LINKS_URL} />;
}

/** The Healthie note: the row's record link, or the hidden wording with no link. */
function HealthieOpenNote({ props }: { props: LinkedRdDialogBodyProps }): ReactElement {
  if (!props.healthieRecordUrl)
    return <div className={NOTICE_CLASS}>{RD_LINKED_HEALTHIE_HIDDEN_NOTE}</div>;

  return <LinkedNote parts={RD_LINKED_HEALTHIE_NOTE} href={props.healthieRecordUrl} />;
}

/**
 * The read-only "still linked" dialog body: one amber note per link that
 * applies, and Close. It writes nothing; the server's flags decide the notes,
 * and the row's shown links only pick their wording.
 */
export function LinkedRdDialogBody(props: LinkedRdDialogBodyProps): ReactElement {
  const Title = props.headerSlots?.Title ?? PlainTitle;
  const Description = props.headerSlots?.Description ?? PlainDescription;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <Title className="text-lg font-semibold text-foreground">
          {rdLinkedTitle(props.rdName)}
        </Title>
        <Description className="text-sm text-muted-foreground">
          {rdLinkedBody(props.rdName)}
        </Description>
      </div>
      {props.removal.signInLinked && <SignInLinkedNote props={props} />}
      {props.removal.healthieOpen && <HealthieOpenNote props={props} />}
      <p className="text-sm text-muted-foreground">{RD_LINKED_CLOSE_LINE}</p>
      <div className="flex justify-end">
        <Button
          id="linked-rd-close"
          type="button"
          variant="outline"
          onClick={props.onClose}
        >
          {RD_CLOSE_ACTION}
        </Button>
      </div>
    </div>
  );
}

export interface TombstonedRdView {
  id: string;
  name: string;
  tombstonedAt: string;
  tombstonedByName: string | null;
  tombstoneReason: string;
  historySummary: string | null;
}

/** "Tombstoned by <name> on <date>", or without the name when none is known. */
export function tombstonedByLine(rd: TombstonedRdView): string {
  const on = formatDate(rd.tombstonedAt);

  return rd.tombstonedByName
    ? `Tombstoned by ${rd.tombstonedByName} on ${on}`
    : `Tombstoned on ${on}`;
}

function TombstonedRow({ rd }: { rd: TombstonedRdView }): ReactElement {
  return (
    <li className="flex flex-col gap-0.5 py-3 border-b border-border last:border-b-0">
      <div className="text-body font-medium text-foreground">{rd.name}</div>
      <div className="text-label text-muted-strong">{tombstonedByLine(rd)}</div>
      <div className="text-label text-muted-strong whitespace-pre-wrap">
        {rd.tombstoneReason}
      </div>
      <div className="text-label text-muted-foreground">
        Still holds: {rd.historySummary ?? "no history"}
      </div>
    </li>
  );
}

/** The collapsed Tombstoned section (AC1): who, when, why and history; no actions, no restore. */
export function TombstonedSection({
  rows,
  open,
  onOpenChange,
}: {
  rows: TombstonedRdView[];
  open: boolean;
  onOpenChange: (next: boolean) => void;
}): ReactElement | null {
  if (rows.length === 0) return null;

  return (
    <Disclosure
      className="mt-6"
      title={RD_TOMBSTONED_SECTION_TITLE}
      badge={
        <span className="text-xs font-medium text-muted-foreground">{rows.length}</span>
      }
      open={open}
      onOpenChange={onOpenChange}
    >
      <ul>
        {rows.map((rd) => (
          <TombstonedRow key={rd.id} rd={rd} />
        ))}
      </ul>
    </Disclosure>
  );
}
